import ReactFacebookLogin from '@greatsumini/react-facebook-login';
import PropTypes from 'prop-types';
import { twMerge } from 'tailwind-merge';

import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';

import { sendEvent } from 'lib/gtag';
import { trackEvent } from 'lib/posthog';
import { getFromSessionStorage } from 'lib/sessionStorage';
import { noop } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import { postSignIn } from 'services/user';

import APP from 'constants/app';
import SOCIAL_MEDIA from 'constants/socialMedia';

const TYPES = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
};

const FacebookLogin = ({
  className,
  gaEventCategory,
  type = TYPES.PRIMARY,
  onSuccess = noop,
  onFailure = noop,
  ...rest
}) => {
  const { login } = useUser();

  const facebookLoginRes = async ({ accessToken }) => {
    let isNewUser = false;

    try {
      const utmCampaign = getFromSessionStorage('utm_campaign');

      const result = await postSignIn({
        oauthToken: accessToken,
        oauthDomain: 'facebook',
        utmCampaign,
      });

      isNewUser = result.isNewUser ?? false;
      await login({ token: result.token });

      const gaEventActionPrefix = isNewUser ? 'signup' : 'login';

      sendEvent({
        action: `${gaEventActionPrefix}_facebook_success`,
        category: gaEventCategory,
      });
      trackEvent(isNewUser ? 'try signup' : 'try login', {
        source_type: 'user',
        status: 'success',
        option: 'facebook',
      });

      onSuccess();
    } catch (err) {
      trackEvent(isNewUser ? 'try signup' : 'try login', {
        source_type: 'user',
        status: 'error',
        option: 'facebook',
        message: Number.isInteger(err?.status) ? String(err.status) : 'unknown',
      });
      onFailure(err);
    }
  };

  return (
    <ReactFacebookLogin
      appId={SOCIAL_MEDIA.FACEBOOK.APP_ID}
      language={APP.DEFAULT_LOCALE}
      render={({ onClick }) => (
        <Button
          aria-label="Login com Facebook"
          className={twMerge(
            '!rounded-3 border-[var(--facebook-primary)] bg-[var(--facebook-primary)] hover:border-[var(--facebook-primary)] hover:bg-[var(--facebook-primary)] active:border-[var(--facebook-primary)] active:bg-[var(--facebook-primary)]',
            type === TYPES.SECONDARY &&
              'border-neutral-high-400 bg-neutral-high-100 text-[var(--facebook-primary)] hover:border-neutral-high-400 hover:bg-neutral-high-100 hover:text-[var(--facebook-primary)] active:border-neutral-high-400 active:bg-neutral-high-100 active:text-[var(--facebook-primary)] active:text-neutral-low-100 [&>span]:text-[var(--facebook-color-primary)]',
            className
          )}
          icon={<Icon name="facebook" />}
          size="size3"
          type="primary"
          onClick={(e) => {
            sendEvent({
              action: 'login_facebook_submit',
              category: gaEventCategory,
            });
            onClick(e);
          }}
          {...rest}
        />
      )}
      onSuccess={facebookLoginRes}
    />
  );
};

FacebookLogin.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(TYPES)),
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
};

export default FacebookLogin;
